/**
 * Argus — modal for per-agent update status & control.
 *
 * Contents:
 *   - Header: PC name + current version + status chip
 *   - Status section: chip color-coded + latestVersionOnChannel
 *   - Auto-update toggle (admin only)
 *   - Channel dropdown (admin only) — bloc agent-channel-control 2026-05-06
 *   - Force Update button (admin only; active ONLY when update available + agent online + not blocked)
 *   - Version history table (paginated)
 *
 * Bloc agent-version-display 2026-05-06.
 * v1.0.4: button state refined (disabled when isAtLatest / in-progress); all UI English.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { dayjs } from '../../lib/dayjsSetup';
import {
  useAgentVersionStatus,
  useAgentUpdateHistory,
  useToggleAutoUpdateMutation,
  useForceUpdateMutation,
  useSetChannelMutation,
  type AgentUpdateHistoryItem,
} from '../../lib/agentVersion';
import { useAuthStore } from '../../lib/auth';
import { ApiError } from '../../lib/api';

interface Props {
  agentId: string;
  pcName: string;
  open: boolean;
  onClose: () => void;
}

const STATUS_COLOR: Record<AgentUpdateHistoryItem['status'], 'success' | 'error' | 'warning' | 'default'> = {
  success: 'success',
  failed: 'error',
  rolled_back: 'warning',
  downloading: 'default',
  verifying: 'default',
  installing: 'default',
};

const IN_PROGRESS_STATUSES: ReadonlySet<AgentUpdateHistoryItem['status']> = new Set([
  'downloading',
  'verifying',
  'installing',
]);

// Window during which we treat Force Update click as "in progress" even if
// no history row has appeared yet. Updater swap + Service restart + first
// heartbeat report typically completes in 30-90s; we use 120s to be safe.
const FORCE_UPDATE_LOCAL_LOCK_MS = 120_000;

export function AgentVersionModal({ agentId, pcName, open, onClose }: Props): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const statusQuery = useAgentVersionStatus(agentId);
  const historyQuery = useAgentUpdateHistory(agentId);

  const toggleMutation = useToggleAutoUpdateMutation(agentId);
  const forceMutation = useForceUpdateMutation(agentId);
  const setChannelMutation = useSetChannelMutation(agentId);

  const [actionFeedback, setActionFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [forceLockUntil, setForceLockUntil] = useState<number>(0);
  const [tickNow, setTickNow] = useState<number>(() => Date.now());

  // 1Hz tick to refresh forceLockUntil countdown comparison + Refetch every second
  // while modal open ensures latest history snapshot.
  useEffect(() => {
    if (!open) return;
    const handle = window.setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [open]);

  const status = statusQuery.data;
  const history = historyQuery.data;

  // Detect ongoing update from history: most recent row with in-progress status.
  const isHistoryInProgress = !!history?.items.find(
    (it) => IN_PROGRESS_STATUSES.has(it.status) && !it.completedAt,
  );

  const isLocallyLocked = tickNow < forceLockUntil;
  const isUpdateInProgress = isHistoryInProgress || isLocallyLocked;

  function handleToggle(disabled: boolean): void {
    setActionFeedback(null);
    toggleMutation.mutate(disabled, {
      onSuccess: () =>
        setActionFeedback({
          kind: 'success',
          text: disabled ? 'Auto-update disabled for this agent.' : 'Auto-update enabled.',
        }),
      onError: (err: unknown) => {
        const msg = err instanceof ApiError ? err.messages[0]?.message : null;
        setActionFeedback({ kind: 'error', text: msg ?? 'Failed to toggle auto-update.' });
      },
    });
  }

  function handleChannelChange(channel: 'dev' | 'staging' | 'prod'): void {
    setActionFeedback(null);
    setChannelMutation.mutate(channel, {
      onSuccess: (data) =>
        setActionFeedback({
          kind: 'success',
          text: `Channel changed: ${data.previousChannel} → ${data.channel}.`,
        }),
      onError: (err: unknown) => {
        const msg = err instanceof ApiError ? err.messages[0]?.message : null;
        setActionFeedback({ kind: 'error', text: msg ?? 'Failed to change channel.' });
      },
    });
  }

  function handleForce(): void {
    setActionFeedback(null);
    setForceLockUntil(Date.now() + FORCE_UPDATE_LOCAL_LOCK_MS);
    forceMutation.mutate(undefined, {
      onSuccess: () =>
        setActionFeedback({
          kind: 'success',
          text: 'Update push sent. Agent will run check-update immediately. Please wait ~60-90s for download + swap + restart.',
        }),
      onError: (err: unknown) => {
        const msg = err instanceof ApiError ? err.messages[0]?.message : null;
        setActionFeedback({ kind: 'error', text: msg ?? 'Failed to push force-update.' });
        // On error, release the local lock so user can retry.
        setForceLockUntil(0);
      },
    });
  }

  const statusChip = (() => {
    if (!status) return null;
    if (status.currentVersion === null) {
      return <Chip label="Unknown version" color="default" size="small" />;
    }
    if (status.isAtLatest) {
      return <Chip label="Up to date" color="success" size="small" />;
    }
    if (status.isOutdated && status.isBlocked) {
      return <Chip label="Outdated & blocked" color="error" size="small" />;
    }
    if (status.isOutdated) {
      return <Chip label="Outdated" color="warning" size="small" />;
    }
    return <Chip label="Unknown status" color="default" size="small" />;
  })();

  // Compute Force Update button state.
  const forceDisabledReason = (() => {
    if (!isAdmin) return 'Admin role required to force update';
    if (!status) return 'Loading status...';
    if (!status.isOnline) return 'Agent offline';
    if (status.updateDisabled) return 'Auto-update disabled for this agent';
    if (status.channelFrozen) return 'Channel frozen — no updates allowed';
    if (status.isAtLatest) return 'Already at latest version on this channel';
    if (!status.latestVersionOnChannel) return 'No manifest published on this channel yet';
    if (isUpdateInProgress) return 'Update in progress — please wait';
    if (forceMutation.isPending) return 'Sending push...';
    return null; // enabled
  })();
  const forceButtonDisabled = forceDisabledReason !== null;

  const forceButtonLabel = (() => {
    if (forceMutation.isPending) return 'Sending...';
    if (isUpdateInProgress) {
      const remainingSec = Math.max(0, Math.ceil((forceLockUntil - tickNow) / 1000));
      return remainingSec > 0 ? `Updating... (${remainingSec}s)` : 'Updating...';
    }
    return 'Force update now';
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 7 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{pcName}</Typography>
          <Typography variant="body2" color="text.secondary">
            Agent update status
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => {
            void statusQuery.refetch();
            void historyQuery.refetch();
          }}
          title="Refresh"
        >
          <RefreshIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {statusQuery.isLoading && <CircularProgress />}
        {statusQuery.isError && (
          <Alert severity="error">{statusQuery.error.message}</Alert>
        )}

        {status && (
          <Stack spacing={2}>
            {/* Status section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                {statusChip}
                <Typography variant="body2">
                  Current version: <strong>{status.currentVersion ?? '—'}</strong>
                </Typography>
                <Typography variant="body2">
                  Channel: <strong>{status.currentChannel ?? '—'}</strong>
                </Typography>
              </Box>
              {status.latestVersionOnChannel && status.latestVersionOnChannel !== status.currentVersion && (
                <Typography variant="caption" color="text.secondary">
                  Latest on channel: <strong>{status.latestVersionOnChannel}</strong>
                </Typography>
              )}
              {status.channelFrozen && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Channel is frozen — no agent receives updates until unfreeze.
                </Alert>
              )}
            </Box>

            <Divider />

            {/* Bloc agent-update-eta 2026-05-07 — auto update checks + ETA. */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Auto update checks
              </Typography>
              {status.lastUpdateCheckAt === null ? (
                <Typography variant="body2" color="text.secondary">
                  Last check: <strong>never</strong> — agent old or freshly enrolled (will check on next reconnect).
                </Typography>
              ) : (() => {
                const fetchedAtMs = statusQuery.dataUpdatedAt || tickNow;
                const elapsedSinceFetch = Math.max(0, (tickNow - fetchedAtMs) / 1000);
                const effectiveAgoSec = (status.lastUpdateCheckSecondsAgo ?? 0) + elapsedSinceFetch;
                const effectiveEtaSec =
                  status.nextCheckEtaSeconds === null
                    ? null
                    : Math.max(0, status.nextCheckEtaSeconds - elapsedSinceFetch);
                const lastCheckMs = tickNow - effectiveAgoSec * 1000;
                return (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Last check: <strong>{dayjs(lastCheckMs).fromNow()}</strong>
                      {' '}
                      <Typography component="span" variant="caption" color="text.disabled">
                        (at {dayjs(lastCheckMs).format('HH:mm')})
                      </Typography>
                    </Typography>
                    {status.isOutdated && !status.isBlocked && status.latestVersionOnChannel && (
                      status.isOnline ? (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Next auto check:{' '}
                          <strong>
                            {effectiveEtaSec === null
                              ? '—'
                              : effectiveEtaSec <= 30
                                ? 'any moment'
                                : dayjs.duration(effectiveEtaSec, 'seconds').humanize(true)}
                          </strong>
                          <Tooltip title="Check may happen sooner on Win+L unlock or via Force update now. Default interval: 4h.">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </Tooltip>
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Next check: <strong>awaiting agent reconnect</strong>
                        </Typography>
                      )
                    )}
                    {status.isAtLatest && (
                      <Typography variant="caption" color="success.main">
                        Agent up to date on current channel — no update pending.
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Box>

            <Divider />

            {/* Control section */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Tooltip title={isAdmin ? '' : 'Admin role required'}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!status.updateDisabled}
                      onChange={(e) => handleToggle(!e.target.checked)}
                      disabled={!isAdmin || toggleMutation.isPending}
                    />
                  }
                  label={status.updateDisabled ? 'Auto-update disabled' : 'Auto-update enabled'}
                />
              </Tooltip>

              <Tooltip title={isAdmin ? '' : 'Admin role required to change channel'}>
                <FormControl size="small" sx={{ minWidth: 140 }} disabled={!isAdmin || setChannelMutation.isPending}>
                  <InputLabel>Channel</InputLabel>
                  <Select
                    value={status.currentChannel ?? 'prod'}
                    label="Channel"
                    onChange={(e) => handleChannelChange(e.target.value as 'dev' | 'staging' | 'prod')}
                  >
                    <MenuItem value="dev">dev</MenuItem>
                    <MenuItem value="staging">staging</MenuItem>
                    <MenuItem value="prod">prod</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>

              <Tooltip title={forceDisabledReason ?? 'Click to push update check immediately'}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleForce}
                    disabled={forceButtonDisabled}
                  >
                    {forceButtonLabel}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {actionFeedback && (
              <Alert severity={actionFeedback.kind} onClose={() => setActionFeedback(null)}>{actionFeedback.text}</Alert>
            )}

            <Divider />

            {/* History section */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Update history
              </Typography>
              {historyQuery.isLoading && <CircularProgress size={20} />}
              {history && history.items.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No updates performed yet.
                </Typography>
              )}
              {history && history.items.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>{it.fromVersion ?? '—'}</TableCell>
                        <TableCell>{it.toVersion}</TableCell>
                        <TableCell>
                          <Chip label={it.status} size="small" color={STATUS_COLOR[it.status]} />
                        </TableCell>
                        <TableCell>{it.startedAt}</TableCell>
                        <TableCell>{it.completedAt ?? '—'}</TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={it.errorMessage ?? ''}
                          >
                            {it.errorMessage?.slice(0, 60) ?? '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
